<template>
  <StepLayout
    class="mb-56"
    stretch
    tracking-id="recap"
    :tracking-model="product.model"
  >
    <template #left>
      <div
        class="left-0 top-0 flex justify-center opacity-100 transition-opacity duration-500 ease-out motion-reduce:transition-none md:sticky"
        :class="{ 'md:opacity-50': !isFocused }"
      >
        <Gallery
          :allow-marketing-images="false"
          :allow-media-viewer="false"
          class="mb-16"
          :images="images"
        />
      </div>
    </template>

    <template #right>
      <div
        class="opacity-100 transition-opacity duration-500 ease-out motion-reduce:transition-none md:pt-72"
        :class="{ 'opacity-50': !isFocused }"
      >
        <h2 class="punchline mb-24 text-center md:text-left">
          {{ i18n(translations.resultTitle) }}
        </h2>

        <div class="mb-24 flex justify-center md:hidden">
          <RevIllustration
            v-if="product.images?.[0]"
            :alt="product.images[0].alt"
            class="mb-16"
            :height="224"
            :src="product.images[0].url"
            :width="224"
          />
        </div>

        <div class="relative">
          <RevIllustration
            alt=""
            class="absolute -right-24 top-[-110px] w-[140px] scale-100 opacity-100 transition-all duration-700 ease-out motion-reduce:transition-none md:right-0 md:top-[-115px]"
            :class="{
              '-translate-y-full scale-90 opacity-0 md:-translate-y-1/2':
                !isFocused,
            }"
            :height="200"
            src="/img/product/funnel/results.svg"
            :width="200"
          />

          <div
            class="bg-surface-default-low border-static-default-hi rounded-sm relative border p-12"
          >
            <slot name="favorites" />
            <div class="flex items-end">
              <h3 class="mr-12 grow">
                <div class="heading-3 flex items-center">
                  {{ product.title }}
                  <div
                    v-if="selectedColorCode"
                    aria-hidden
                    class="rounded-full ml-8 size-[12px] shrink-0"
                    :style="{ background: selectedColorCode }"
                  ></div>
                </div>
                <div class="body-2 mt-4">
                  {{ selectedTitle }}
                </div>
              </h3>
              <Price
                class="shrink-0 items-end"
                layout="vertical"
                :offer="grade"
                :product="product"
                tooltip-position="top"
              />
            </div>

            <RevButton
              class="relative left-1/2 my-16 !max-w-[372px] -translate-x-1/2"
              full-width="always"
              variant="primary"
              @click="onAddToCart"
            >
              {{ i18n(translations.resultAddToCart) }}
            </RevButton>

            <div class="text-static-default-low body-2 text-center">
              <p class="flex items-center justify-center">
                <FormattedMessage
                  class="truncate"
                  :definition="translations.resultSeller"
                  tag="span"
                >
                  <template #seller>
                    <RevLink
                      v-if="grade.merchant"
                      :to="grade.merchant.link"
                      @click="onSellerClick"
                    >
                      {{ grade.merchant?.company }}
                    </RevLink>
                  </template>
                </FormattedMessage>

                <Flag
                  v-if="shouldDisplaySellerFlag"
                  :aria-label="grade.merchant.country"
                  class="ml-8 shrink-0"
                  :country-code="
                    grade.merchant.countryCode as MarketCountryCode
                  "
                />

                <span v-if="shouldDisplaySellerCountry" class="ml-4 shrink-0">
                  ({{ grade.merchant.country }})
                </span>
              </p>

              <ClientOnly>
                <p class="mb-8 mt-16 flex justify-center">
                  <PaymentIcons :methods="paymentMethods" />
                </p>
              </ClientOnly>
            </div>
          </div>

          <div
            v-if="shouldDisplayTradeIn"
            class="flex items-center justify-center py-8"
          >
            <IconPlus />
          </div>

          <div
            v-if="shouldDisplayTradeIn"
            class="bg-surface-default-low border-static-default-hi rounded-sm relative border p-12"
          >
            <div class="flex items-center gap-16">
              <RevIllustration
                alt=""
                class="rounded-sm"
                :height="54"
                src="/img/product/funnel/guidance-trade-in.svg"
                :width="54"
              />
              <div class="body-2-bold">
                <h3>
                  {{
                    i18n(translations.tradeInProposalName, {
                      device: swapOffer?.model,
                    })
                  }}
                </h3>
                <p class="text-action-default-low mt-8">
                  {{
                    i18n(translations.tradeInProposalPrice, {
                      price: swapOffer?.offerPrice,
                    })
                  }}
                </p>
              </div>
            </div>

            <p class="caption mt-12">
              {{
                i18n(translations.tradeInProposalDescription, {
                  price: swapOffer?.offerPrice,
                })
              }}
            </p>
          </div>
        </div>

        <div class="body-2-bold mb-56 mt-24 flex flex-col gap-12">
          <ItemShipping :shipping="grade.reassurance?.shipping" />
          <ItemCarriers
            :carriers="product.compatibleCarriers"
            :product-brand="product.trackingDetails.brand"
            :product-category="
              product.trackingDetails.category3MarketplaceCategory_ID
            "
          />
          <ItemReassurance
            :warranty-delay="grade.reassurance?.defaultWarrantyDelay"
          />

          <CopyLevy class="mt-24" :copy-levy="grade.copyLevy" />
          <Vat v-if="isVatIncluded" class="mt-24" />
        </div>
      </div>

      <slot name="details" />
    </template>
  </StepLayout>
</template>

<script lang="ts" setup>
import { computed } from 'vue'

import { MarketCountryCode, productAPI } from '@backmarket/http-api'
import FormattedMessage from '@backmarket/nuxt-module-i18n/FormattedMessage.vue'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useMarketplace } from '@backmarket/nuxt-module-marketplace/useMarketplace'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { isEmpty } from '@backmarket/utils/object/isEmpty'
import { RevButton } from '@ds/components/Button'
import { RevIllustration } from '@ds/components/Illustration'
import { RevLink } from '@ds/components/Link'
import { IconPlus } from '@ds/icons/IconPlus'

import { type Estimation } from '~/scopes/buyback/api/adapters/getEstimationSwapPrice.adapter'
import { SWAP_SUCCESS } from '~/scopes/buyback/swap/components/SwapBlock/constants'
import PaymentIcons from '~/scopes/payment/scopes/methods/components/PaymentIcons'
import { usePaymentMethodsWithFallback } from '~/scopes/payment/scopes/methods/composables/usePaymentMethodsWithFallback'

import { useAddToCart } from '../../../../composables/useAddToCart'
import { useProductTracking } from '../../../../composables/useProductTracking'
import CopyLevy from '../../../CopyLevy/CopyLevy.vue'
import Gallery from '../../../Gallery/Gallery.vue'
import Price from '../../../Price/Price.vue'
import ItemCarriers from '../../../ReassuranceItems/components/ItemCarriers/ItemCarriers.vue'
import ItemReassurance from '../../../ReassuranceItems/components/ItemReassurance/ItemReassurance.vue'
import ItemShipping from '../../../ReassuranceItems/components/ItemShipping/ItemShipping.vue'
import Vat from '../../../Vat/Vat.vue'
import translations from '../../CustomizationFunnel.translations'
import StepLayout from '../StepLayout/StepLayout.vue'

const COUNTRIES_WITH_SELLER_FLAG = [MarketCountryCode.US, MarketCountryCode.AU]

const props = withDefaults(
  defineProps<{
    grade: productAPI.GetBestOffersResponse[number]
    isFocused?: boolean
    product: productAPI.GetProductResponse
    selectedTitle: string
    selectedColorCode?: string
    swapStatus?: string
    swapOffer?: Estimation
    tracking: ReturnType<typeof useProductTracking>
  }>(),
  {
    isFocused: true,
    selectedColorCode: undefined,
    swapOffer: undefined,
    swapStatus: '',
  },
)

const { trackClick } = useTracking()
const marketplace = useMarketplace()
const { addToCart } = useAddToCart()
const i18n = useI18n()
const paymentMethods = usePaymentMethodsWithFallback(
  () => props.grade.paymentMethods,
)

const isVatIncluded = computed(() => {
  return marketplace.features.vatIncluded
})

const shouldDisplaySellerFlag = computed(() => {
  return (
    COUNTRIES_WITH_SELLER_FLAG.includes(marketplace.market.countryCode) &&
    !!props.grade.merchant?.countryCode
  )
})

const shouldDisplaySellerCountry = computed(() => {
  return !!props.grade.merchant?.country && !shouldDisplaySellerFlag.value
})

const images = computed(() => {
  return props.product.images.slice(0, 3)
})

function onAddToCart() {
  if (props.grade) {
    addToCart({
      listingId: props.grade.id,
      tracking: {
        ...props.tracking.product.value,
        list: 'recap',
      },
      swapStatus: props.swapStatus,
    })
  }
}

function onSellerClick() {
  trackClick({
    zone: 'recap',
    name: 'seller',
    value:
      props.grade.merchant?.countryCode || props.grade.merchant?.country || '',
  })
}

const shouldDisplayTradeIn = computed(() => {
  return (
    props.swapStatus === SWAP_SUCCESS &&
    !isEmpty(props.swapOffer) &&
    props.swapOffer?.hasOffer
  )
})
</script>
